import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AsyncAction } from '../../../core/async-state/models/async-action.model';
import { handleErrors } from '../../../core/async-state/operators/handle-errors';
import {
  ApiResponse,
  GeneralHealthHistoryData,
  SymptomHistoryData,
  WeightHistoryData,
} from '../../../shared/models';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { UserDataApiActions } from '../actions/user-data-api.actions';
import { UserDataApiService } from '../services/user-data-api.service';
import { BasicWebkData, GetDataAvailabilityResponse } from 'ngx-esprio-shared';

@Injectable()
export class UserDataEffects {
  getUserWeightHistoryData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.getUserWeightHistoryData),
        switchMap((action) =>
          this.apiService
            .getUserWeightHistoryData(
              action.userId,
              action.monitoredUserId,
              action.startDate,
              action.endDate
            )
            .pipe(
              handleErrors(() => UserDataApiActions.getUserWeightHistoryDataFail()),
              map((res: ApiResponse<WeightHistoryData[]>) =>
                UserDataApiActions.getUserWeightHistoryDataSuccess({ payload: res.data })
              ),
              catchError((errAction: AsyncAction) => of(errAction))
            )
        )
      ),
    { dispatch: true }
  );

  getUserBasicWebkData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.getUserBasicWebkData),
        switchMap((action) =>
          this.apiService
            .getUserBasicWebkData(
              action.userId,
              action.monitoredUserId,
              action.startDate,
              action.endDate
            )
            .pipe(
              handleErrors(() => UserDataApiActions.getUserBasicWebkDataFail()),
              map((res: ApiResponse<BasicWebkData[]>) =>
                UserDataApiActions.getUserBasicWebkDataSuccess({ payload: res.data })
              ),
              catchError((errAction: AsyncAction) => of(errAction))
            )
        )
      ),
    { dispatch: true }
  );

  getUserSymptomsHistoryData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.getUserSymptomsHistoryData),
        mergeMap((action) =>
          this.apiService
            .getSymptomsHistoryData(
              action.userId,
              action.monitoredUserId,
              action.startDate,
              action.endDate,
              action.submissionId,
              action.questionGroup
            )
            .pipe(
              handleErrors(() => UserDataApiActions.getUserSymptomsHistoryDataFail()),
              map((res: ApiResponse<SymptomHistoryData[]>) =>
                UserDataApiActions.getUserSymptomsHistoryDataSuccess({
                  questionGroup: action.questionGroup,
                  payload: res.data,
                })
              ),
              catchError((errAction: AsyncAction) => of(errAction))
            )
        )
      ),
    { dispatch: true }
  );

  getUserGeneralHealthHistoryData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.getUserGeneralHealthHistoryData),
        switchMap((action) =>
          this.apiService
            .getGeneralHealthHistoryData(
              action.userId,
              action.monitoredUserId,
              action.startDate,
              action.endDate
            )
            .pipe(
              handleErrors(() => UserDataApiActions.getUserGeneralHealthHistoryDataFail()),
              map((res: ApiResponse<GeneralHealthHistoryData[]>) =>
                UserDataApiActions.getUserGeneralHealthHistoryDataSuccess({ payload: res.data })
              ),
              catchError((errAction: AsyncAction) => of(errAction))
            )
        )
      ),
    { dispatch: true }
  );

  getUserWeightChangePercentage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.getUserWeightChangePercentage),
        switchMap((action) =>
          this.apiService
            .getUserWeightChangePercentage(
              action.userId,
              action.monitoredUserId,
              action.startDate,
              action.endDate
            )
            .pipe(
              handleErrors(() => UserDataApiActions.getUserWeightChangePercentageFail()),
              map(
                (
                  res: ApiResponse<{
                    weightChangePercentage: number;
                    startDate: string;
                    endDate: string;
                  }>
                ) =>
                  UserDataApiActions.getUserWeightChangePercentageSuccess({
                    payload: {
                      weightChangePercentage: res.data.weightChangePercentage,
                      startDate: new Date(res.data.startDate),
                      endDate: new Date(res.data.endDate),
                    },
                  })
              ),
              catchError((errAction: AsyncAction) => of(errAction))
            )
        )
      ),
    { dispatch: true }
  );

  getUserTimeSnap$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.getUserTimeSnap),
        switchMap((action) =>
          this.apiService
            .getUserTimeSnap(action.userId, action.monitoredUserId, action.referenceDate)
            .pipe(
              handleErrors(() => UserDataApiActions.getUserTimeSnapFail()),
              map((res: ApiResponse<WeightHistoryData>) =>
                UserDataApiActions.getUserTimeSnapSuccess({ payload: res.data })
              ),
              catchError((errAction: AsyncAction) => of(errAction))
            )
        )
      ),
    { dispatch: true }
  );

  public getUserScoreDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.getUserScoreDetails),
        switchMap(({ userId, monitoredUserId, submissionId }) =>
          this.apiService.getUserScoreDetails(userId, monitoredUserId, submissionId).pipe(
            handleErrors(() => UserDataApiActions.getUserScoreDetailsFail()),
            map((res: ApiResponse<any>) =>
              UserDataApiActions.getUserScoreDetailsSuccess({ payload: res.data })
            ),
            catchError((errorAction: AsyncAction) => of(errorAction))
          )
        )
      ),
    { dispatch: true }
  );

  checkDataAvailability$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserDataApiActions.checkDataAvailability),
        switchMap(({ userId, monitoredUserId }) =>
          this.apiService.checkDataAvailability(userId, monitoredUserId).pipe(
            handleErrors(() => UserDataApiActions.checkDataAvailabilityFail()),
            map((res: ApiResponse<GetDataAvailabilityResponse>) =>
              UserDataApiActions.checkDataAvailabilitySuccess({ payload: res.data })
            ),
            catchError((errAction: AsyncAction) => of(errAction))
          )
        )
      ),
    { dispatch: true }
  );

  constructor(private actions$: Actions, private apiService: UserDataApiService) {}
}
